import { Inject, Injectable } from '@angular/core';
import { combineLatest, defer, from, mergeMap, Observable, of, switchMap, throwError } from 'rxjs';
import { ENDPOINTS } from '../../enums/endpoints.enum';
import { UriUtilitiesService } from '../uri-utilities/uri-utilities.service';
import { UserGidService } from '../user-gid/user-gid.service';
import { HttpClient } from '@angular/common/http';
import { catchError, map, take, tap } from 'rxjs/operators';
import { LogInstanceToken, LogInterface, PaceInsightsService } from '@evorait/pace-logging';
import { ConcurrentStorageData } from '../../types/concurrent-usage';
import { GWRequestType, GWResponseType } from './concurrent-usage.type';
import { EncodingUtilities } from '../../utilities/encoding-utilities';
import { getUuidV4 } from '../../utilities/generic-utilities';
import { safeStringify } from '@evorait/pace-components';
import { ConcurrentUsageDbService } from './concurrent-usage-db.service';
import { DEVICE_LOCK_KEY } from '../device/device.utils';

@Injectable({ providedIn: 'root' })
export class ConcurrentUsageService {
    private url: string;

    public constructor(
        private readonly http: HttpClient,
        private readonly uriUtilities: UriUtilitiesService,
        private readonly userGidService: UserGidService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly paceInsights: PaceInsightsService,
        private readonly concurrentUsageDbService: ConcurrentUsageDbService,
    ) {
        // URL for posting single key value pair or GETTING values from key value store
        this.url = this.uriUtilities.getUriForContent() + ENDPOINTS.UserProfileSet;
    }

    public downloadAll(): Observable<unknown> {
        // defer ensures a fresh start timestamp for every subscription (important if method is reused).
        return defer(() => {
            const start = performance.now(); // high-resolution timestamp
            return this.http.get<GWResponseType>(this.url + `?$format=json`).pipe(
                map((res) => {
                    this.log.debug(`ConcurrentUsageService: downloadAll: SUCCESS loading all data`);
                    return res.d.results
                        .filter((entry) => !!entry?.value && entry.value.length > 0)
                        .map((entry) => JSON.parse(EncodingUtilities.base64DecodeUnicode(entry.value)));
                }),
                mergeMap(async (results: ConcurrentStorageData[]) => {
                    const data = results
                        .filter((result) => typeof result === 'object' && !!result.key && !!result.value)
                        .reduce(
                            (acc, curr) => {
                                acc[curr.key] = curr.value;
                                return acc;
                            },
                            {} as { [key: string]: string },
                        );
                    this.paceInsights.trackEvent('ConcurrentUsageService::downloadAll::result', {
                        ...data,
                        durationMs: Math.round(performance.now() - start),
                    });
                    await this.concurrentUsageDbService.replaceAll(results);
                }),
            );
        });
    }

    public uploadAll(): Observable<unknown> {
        return combineLatest([this.uploadPresentEntries(), this.uploadDeletedEntries()]);
    }

    public writeDataForKey(key: string, value: string, timestamp: number): Observable<ConcurrentStorageData> {
        const concurrencyData: ConcurrentStorageData = { key, value, timestamp };
        this.log.debug(`ConcurrentUsageService: writeDataForKey: writing data for key "${key}"`);
        this.paceInsights.trackEvent(`ConcurrentUsageService:writeDataForKey:${key}`, {
            concurrencyData,
        });

        // store locally
        return from(this.concurrentUsageDbService.put(concurrencyData)).pipe(map(() => concurrencyData));
    }

    public readDataForKey(key: string): Observable<ConcurrentStorageData[]> {
        this.log.debug(`ConcurrentUsageService: readDataForKey: reading data for key "${key}"`);

        return from(this.concurrentUsageDbService.get(key)).pipe(
            take(1),
            map((data) => {
                if (!data?.value) {
                    return [];
                }
                return [data];
            }),
        );
    }

    /**
     * Binary-payload variant of `writeDataForKey`. Maps the bytes to a latin1 string so they
     * travel through the same encrypt-and-persist (and remote-sync) path as string values.
     *
     * Why binary payloads are carried as a latin1 string rather than stored as raw bytes:
     *
     * This store is not local-only — it is mirrored to the backend. `uploadAll` reads every
     * present entry, `writeRemote` `JSON.stringify`s each `{ key, value, timestamp }` envelope,
     * and `downloadAll` -> `ConcurrentUsageDbService.replaceAll` CLEARS the local table and
     * rebuilds it from the remote results. So an entry only survives a download if it can
     * round-trip through that string/JSON transport; a raw `Uint8Array` value cannot, and would
     * be wiped on the next `downloadAll` (triggered by the device-lock flow). The whole pipeline
     * — `ConcurrentStorageData.value`, `EncryptionService.encryptAsync`, the GW envelope — is
     * therefore string-typed by design.
     *
     * Mapping each byte to a latin1 code unit is the minimal lossless way to push compressed
     * bytes through that string pipeline: code points 0-255 have no surrogates, so they survive
     * `encryptAsync` (utf-8) and `base64EncodeUnicode` (`encodeURIComponent`) unchanged. The
     * single base64 the transport already applies is the only base64; we add none here.
     *
     * If the service is ever refactored to send binary data to the backend (binary GW envelope
     * + a binary-aware `replaceAll`), this latin1 hop — and the string-typed value — can be
     * revisited in favour of storing/transporting raw bytes.
     *
     * @param key the storage key
     * @param value the binary payload to store
     * @param timestamp the write timestamp
     * @returns the stored concurrency data
     */
    public writeBinaryDataForKey(
        key: string,
        value: ArrayBuffer,
        timestamp: number,
    ): Observable<ConcurrentStorageData> {
        return this.writeDataForKey(key, ConcurrentUsageService.bytesToBinaryString(value), timestamp);
    }

    /**
     * Binary-payload variant of `readDataForKey`. Reverses the latin1 mapping applied by
     * `writeBinaryDataForKey`; empty when nothing is stored for the key.
     *
     * @param key the storage key
     * @returns the stored bytes, or an empty array when nothing is stored
     */
    public readBinaryDataForKey(key: string): Observable<ArrayBuffer[]> {
        return this.readDataForKey(key).pipe(
            map((entries) => entries.map((entry) => ConcurrentUsageService.binaryStringToBytes(entry.value))),
        );
    }

    /**
     * Maps each byte to a code unit (latin1); the inverse of `binaryStringToBytes`.
     *
     * @param bytes the bytes to encode
     * @returns the latin1 string carrying the bytes
     */
    private static bytesToBinaryString(bytes: ArrayBuffer): string {
        let binary = '';
        for (const byte of new Uint8Array(bytes)) {
            binary += String.fromCharCode(byte);
        }
        return binary;
    }

    /**
     * Reads each code unit back to a byte (latin1); the inverse of `bytesToBinaryString`.
     *
     * @param value the latin1 string to decode
     * @returns the decoded bytes
     */
    private static binaryStringToBytes(value: string): ArrayBuffer {
        const bytes = new Uint8Array(value.length);
        for (let index = 0; index < value.length; index++) {
            bytes[index] = value.charCodeAt(index);
        }
        return bytes.buffer;
    }

    public getAllPresentEntries(): Observable<ConcurrentStorageData[]> {
        this.log.debug('ConcurrentUsageService: getAllPresentEntries: loading all present entries');
        return from(this.concurrentUsageDbService.getPresentEntries());
    }

    public liveQueryDataForKey$(key: string): Observable<ConcurrentStorageData | undefined> {
        return this.concurrentUsageDbService.liveQuery$(key);
    }

    public deleteDataForKey(key: string): Observable<unknown> {
        this.log.debug(`ConcurrentUsageService: deleteDataForKey: deleting key "${key}"`);
        return from(this.concurrentUsageDbService.remove(key));
    }

    private uploadDeletedEntries(): Observable<unknown> {
        this.log.debug('ConcurrentUsageService::uploadDeletedEntries');
        return from(this.concurrentUsageDbService.getDeletedEntries()).pipe(
            switchMap((allKeys: string[]) => {
                this.log.debug(
                    `ConcurrentUsageService: uploadDeletedEntries: uploading deletion of ${allKeys.join(', ')} (${allKeys.length}) entries to remote`,
                );

                if (allKeys.length === 0) {
                    return of(true);
                }

                return combineLatest(allKeys.map((key) => this.deleteRemote(key)));
            }),
        );
    }

    private uploadPresentEntries(): Observable<unknown> {
        this.log.debug('ConcurrentUsageService::uploadPresentEntries');
        return from(this.concurrentUsageDbService.getPresentEntries()).pipe(
            map((allEntries) => allEntries.filter((entry) => entry.key !== DEVICE_LOCK_KEY)),
            switchMap((allEntries: ConcurrentStorageData[]) => {
                this.log.debug(
                    `ConcurrentUsageService: uploadAll: uploading ${allEntries.length} entries to remote: ${allEntries.map((entry) => entry.key).join(', ')}`,
                );

                if (allEntries.length === 0) {
                    return of(true);
                }

                return combineLatest(allEntries.map((entry) => this.writeRemote(entry)));
            }),
        );
    }

    /**
     * Reads concurrency data explicitly from remote store.
     *
     * Please note, this will not read from local storage, use `readDataForKey` for general usage.
     *
     * @param key The key to read.
     * @returns Observable<ConcurrentStorageData[]> An observable that emits the concurrency data read from remote store.
     */
    public readRemote(key: string): Observable<ConcurrentStorageData[]> {
        this.log.debug(`ConcurrentUsageService: readRemote: loading data for key "${key}"`);
        const id = getUuidV4();
        this.paceInsights.trackEvent('ConcurrentUsageService::readRemote', { id, key, state: 'attempt' });
        const start = performance.now(); // high-resolution timestamp

        return this.http
            .get<GWResponseType>(this.url + `?$filter=key eq '${this.generateKeyForUser(key)}'&$format=json`)
            .pipe(
                map((res) => {
                    this.log.debug(`ConcurrentUsageService: readDataForKey: SUCCESS loading data for key "${key}"`);
                    return res.d.results
                        .filter((entry) => entry.value && entry.value.length > 0)
                        .map((entry) => JSON.parse(EncodingUtilities.base64DecodeUnicode(entry.value)));
                }),
                tap((response) => {
                    this.paceInsights.trackEvent('ConcurrentUsageService::readDataForKey', {
                        state: 'success',
                        id,
                        key,
                        response: safeStringify(response),
                        durationMs: Math.round(performance.now() - start),
                    });
                }),
                catchError((error) => {
                    this.paceInsights.trackEvent('ConcurrentUsageService::readDataForKey', {
                        state: 'error',
                        id,
                        key,
                        error: safeStringify(error),
                        durationMs: Math.round(performance.now() - start),
                    });
                    const errorMessage = `ConcurrentUsageService: readDataForKey: ERROR data with following key could not be read from UserProfileSet: ${key}. Error ${safeStringify(error)}`;
                    this.log.error(errorMessage);
                    return throwError(() => new Error(errorMessage));
                }),
            );
    }

    /**
     * Writes concurrency data explicitly to remote store.
     *
     * Please note, this will not write into local storage, use `writeDataForKey` for general usage.
     *
     * @param concurrencyData The concurrency data to write.
     * @returns Observable<unknown> An observable that completes when the write operation is done.
     */
    public writeRemote(concurrencyData: ConcurrentStorageData): Observable<unknown> {
        const body: GWRequestType = {
            key: this.generateKeyForUser(concurrencyData.key),
            value: EncodingUtilities.base64EncodeUnicode(JSON.stringify(concurrencyData)),
            gid: this.userGidService.getUserGid(),
        };
        this.log.debug(
            `ConcurrentUsageService: uploadRemote: writing data for key "${concurrencyData.key}" at timestamp "${concurrencyData.timestamp}"`,
        );
        const id = getUuidV4();
        this.paceInsights.trackEvent('ConcurrentUsageService::uploadRemote', {
            state: 'attempt',
            id,
            key: concurrencyData.key,
            value: concurrencyData.value,
        });
        const start = performance.now(); // high-resolution timestamp

        return this.http.post<void>(this.url, body).pipe(
            tap(() => {
                this.log.debug(
                    `ConcurrentUsageService: uploadRemote: SUCCESS writing data for key "${concurrencyData.key}" at timestamp "${concurrencyData.timestamp}"`,
                );
                this.paceInsights.trackEvent('ConcurrentUsageService::uploadRemote', {
                    state: 'success',
                    id,
                    key: concurrencyData.key,
                    durationMs: Math.round(performance.now() - start),
                });
            }),
            catchError((error) => {
                this.paceInsights.trackEvent('ConcurrentUsageService::uploadRemote', {
                    state: 'error',
                    id,
                    key: concurrencyData.key,
                    error: safeStringify(error),
                    durationMs: Math.round(performance.now() - start),
                });
                const errorMessage = `ConcurrentUsageService: uploadRemote: ERROR data with following key could not be written to UserProfileSet: ${concurrencyData.key}. Error ${safeStringify(error)}`;
                this.log.error(errorMessage);
                return throwError(() => new Error(errorMessage));
            }),
        );
    }

    /**
     * Deletes concurrency data explicitly from remote store.
     *
     * Please note, this will not delete from local storage, use `deleteDataForKey` for general usage.
     *
     * @param key The key to delete.
     * @returns Observable<unknown> An observable that completes when the delete operation is done.
     */
    public deleteRemote(key: string): Observable<unknown> {
        // TODO: Gateway is lacking the delete feature
        // For now we are filtering the returned values for non-empty entries
        const body: GWRequestType = {
            key: this.generateKeyForUser(key),
            value: '',
            gid: this.userGidService.getUserGid(),
        };
        this.log.debug(`ConcurrentUsageService: deleteRemote: removing data for key "${key}"`);
        const id = getUuidV4();
        this.paceInsights.trackEvent('ConcurrentUsageService::deleteRemote', { id, key, state: 'attempt' });
        const start = performance.now(); // high-resolution timestamp

        return this.http.post<void>(this.url, body).pipe(
            tap(() => {
                this.log.debug(`ConcurrentUsageService: deleteRemote: SUCCESS removing data for key "${key}"`);
                this.paceInsights.trackEvent('ConcurrentUsageService::deleteRemote', {
                    state: 'success',
                    id,
                    key,
                    durationMs: Math.round(performance.now() - start),
                });
            }),
            catchError((error) => {
                this.paceInsights.trackEvent('ConcurrentUsageService::deleteRemote', {
                    state: 'error',
                    id,
                    key,
                    error: safeStringify(error),
                    durationMs: Math.round(performance.now() - start),
                });
                const errorMessage = `ConcurrentUsageService: deleteRemote: ERROR data with following key could not be deleted from UserProfileSet: ${key}. Error ${safeStringify(error)}`;
                this.log.error(errorMessage);
                return throwError(() => new Error(errorMessage));
            }),
        );
    }

    private generateKeyForUser(key: string): string {
        return `${this.userGidService.getUserGid()}-${key}`;
    }
}
